import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Users, 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  Loader2,
  UserPlus,
  Shield,
  ShieldCheck,
  User as UserIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = {
  platform_owner: { label: "Platform Owner", color: "bg-purple-100 text-purple-700", icon: ShieldCheck },
  company_admin: { label: "Company Admin", color: "bg-blue-100 text-blue-700", icon: Shield },
  user: { label: "User", color: "bg-gray-100 text-gray-700", icon: UserIcon },
};

export default function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "user",
    company_id: "",
  });

  const isPlatformOwner = currentUser?.role === "platform_owner";
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);

      if (isPlatformOwner) {
        const companiesRes = await axios.get(`${API}/admin/companies`, { headers: { Authorization: `Bearer ${token}` } });
        setCompanies(companiesRes.data);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.email || !formData.name || (!editingUser && !formData.password)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(
          `${API}/admin/users/${editingUser.id}`,
          { name: formData.name, role: formData.role, is_active: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("User updated");
      } else {
        const params = new URLSearchParams({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          role: formData.role,
        });
        if (formData.company_id) params.append("company_id", formData.company_id);
        
        await axios.post(`${API}/admin/users?${params}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("User created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: "",
      role: user.role || "user",
      company_id: user.company_id || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleDeactivate = async (userId, isActive) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { is_active: !isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isActive ? "User deactivated" : "User activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      name: "",
      password: "",
      role: "user",
      company_id: "",
    });
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleInfo = (role) => ROLES[role] || ROLES.user;

  return (
    <div className="space-y-8" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            User Management
          </h1>
          <p className="text-[#5c6b63] mt-2">
            {isPlatformOwner ? "Manage all platform users" : "Manage users in your company"}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingUser ? "Edit User" : "Add New User"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@company.com"
                  disabled={!!editingUser}
                  className="h-11"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="h-11"
                  data-testid="user-name-input"
                />
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="h-11"
                    data-testid="user-password-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger className="h-11" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="company_admin">Company Admin</SelectItem>
                    {isPlatformOwner && <SelectItem value="platform_owner">Platform Owner</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {isPlatformOwner && (
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })}>
                    <SelectTrigger className="h-11" data-testid="user-company-select">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">No Company</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full btn-accent h-11"
                data-testid="save-user-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingUser ? "Update User" : "Create User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="kpi-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5c6b63]">Total Users</p>
                  <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">{stats.total_users}</p>
                </div>
                <Users className="w-8 h-8 text-[#00c853]" />
              </div>
            </CardContent>
          </Card>
          <Card className="kpi-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5c6b63]">Active Users</p>
                  <p className="text-2xl font-bold text-emerald-600 font-['Outfit']">{stats.active_users}</p>
                </div>
                <UserIcon className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          {isPlatformOwner && (
            <Card className="kpi-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#5c6b63]">Companies</p>
                    <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">{stats.total_companies}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="kpi-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5c6b63]">Transactions</p>
                  <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">{stats.total_transactions}</p>
                </div>
                <Shield className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c6b63]" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-users"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            All Users ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63]">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    {isPlatformOwner && <TableHead>Company</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const RoleIcon = roleInfo.icon;
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-[#5c6b63]">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={`${roleInfo.color} flex items-center gap-1 w-fit`}>
                            <RoleIcon className="w-3 h-3" />
                            {roleInfo.label}
                          </Badge>
                        </TableCell>
                        {isPlatformOwner && (
                          <TableCell className="text-[#5c6b63]">{user.company_name || "-"}</TableCell>
                        )}
                        <TableCell>
                          <Badge className={user.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                            {user.is_active !== false ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(user)}
                              disabled={user.id === currentUser?.id}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600"
                              onClick={() => handleDelete(user.id)}
                              disabled={user.id === currentUser?.id}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
